import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, ArrowUpRight, ArrowDownRight, Radio, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtUsd } from "@/lib/api";
import { cn } from "@/lib/utils";

const SentimentBadge = ({ sentiment }) => {
  const map = {
    Bullish: "bg-[#00FFA3]/10 text-[var(--pos)] border-[#00FFA3]/30",
    Bearish: "bg-[#FF3366]/10 text-[var(--neg)] border-[#FF3366]/30",
    Neutral: "bg-[#A1A1AA]/10 text-[var(--text-dim)] border-[#A1A1AA]/30",
  };
  const tr = { Bullish: "Yükseliş", Bearish: "Düşüş", Neutral: "Nötr" };
  return (
    <span className={cn("text-[10px] px-2 py-0.5 rounded border font-mono uppercase tracking-wider", map[sentiment] || map.Neutral)}>
      {tr[sentiment] || sentiment}
    </span>
  );
};

const SignalCard = ({ signal, onApprove, onReject, busy }) => {
  const isBuy = signal.action === "BUY";
  const accent = isBuy ? "#00FFA3" : "#FF3366";
  const isPending = signal.status === "pending";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.25 }}
      data-testid={`signal-card-${signal.symbol}`}
      className="rounded-lg border bg-[var(--surface)] p-5"
      style={{ borderColor: isPending ? `${accent}55` : "var(--border)" }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div
            className="h-9 w-9 rounded-md flex items-center justify-center"
            style={{ background: `${accent}1A` }}
          >
            {isBuy ? <ArrowUpRight className="h-5 w-5" style={{ color: accent }} />
                   : <ArrowDownRight className="h-5 w-5" style={{ color: accent }} />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-head font-bold text-base">{signal.symbol}</span>
              <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded" style={{ color: accent, background: `${accent}15` }}>
                {isBuy ? "ALIM" : "SATIM"}
              </span>
            </div>
            <div className="text-[11px] text-[var(--text-faint)] font-mono">@ {fmtUsd(signal.entry_price)}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-[var(--text-faint)] uppercase tracking-wider">Güven</div>
          <div className="font-mono font-bold text-lg" style={{ color: accent }}>{signal.confidence}%</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <SentimentBadge sentiment={signal.sentiment} />
        <span className="text-[10px] text-[var(--text-faint)] font-mono">İşlem: {fmtUsd(signal.suggested_usdt)}</span>
      </div>

      <p className="text-[13px] leading-relaxed text-[var(--text-dim)] mb-4 border-l-2 pl-3" style={{ borderColor: `${accent}55` }}>
        {signal.reasoning}
      </p>

      {isPending ? (
        <div className="flex gap-2">
          <Button
            data-testid={`approve-signal-${signal.symbol}`}
            onClick={() => onApprove(signal)}
            disabled={busy}
            className="flex-1 h-12 font-bold text-black hover:opacity-90"
            style={{ background: accent }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Onayla & İşle</>}
          </Button>
          <Button
            data-testid={`reject-signal-${signal.symbol}`}
            onClick={() => onReject(signal)}
            disabled={busy}
            variant="outline"
            className="h-12 w-12 p-0 border-[var(--border)] text-[var(--text-dim)] hover:bg-[#FF3366]/10 hover:text-[var(--neg)] hover:border-[#FF3366]/40"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className={cn("text-center py-2 rounded-md text-xs font-mono uppercase tracking-wider",
          signal.status === "executed" ? "bg-[#00FFA3]/10 text-[var(--pos)]" : "bg-[var(--surface-hover)] text-[var(--text-faint)]")}>
          {signal.status === "executed" ? `✓ İşlem Gerçekleşti @ ${fmtUsd(signal.executed_price)}` : "✕ Reddedildi"}
        </div>
      )}
    </motion.div>
  );
};

export const SignalFeed = ({ signals, onApprove, onReject }) => {
  const [busyId, setBusyId] = useState(null);
  const pending = signals.filter((s) => s.status === "pending");
  const resolved = signals.filter((s) => s.status !== "pending");

  const wrap = (fn) => async (signal) => {
    setBusyId(signal.id);
    try { await fn(signal); } finally { setBusyId(null); }
  };

  return (
    <div data-testid="signal-feed" className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)]">
      <div className="flex items-center justify-between px-5 h-14 border-b border-[var(--border)]">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-[var(--accent)]" />
          <h3 className="font-head font-bold text-base">AI İşlem Sinyalleri</h3>
        </div>
        {pending.length > 0 && (
          <Badge className="bg-[#A374FF]/15 text-[var(--accent)] border-[#A374FF]/30 font-mono animate-pulse">
            {pending.length} bekliyor
          </Badge>
        )}
      </div>

      <div className="p-4 space-y-3 max-h-[640px] overflow-y-auto">
        {signals.length === 0 && (
          <div className="text-center py-12 text-[var(--text-faint)]">
            <Radio className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Henüz sinyal yok.</p>
            <p className="text-xs mt-1">"Piyasayı Analiz Et" ile başlayın.</p>
          </div>
        )}
        <AnimatePresence mode="popLayout">
          {pending.map((s) => (
            <SignalCard key={s.id} signal={s} onApprove={wrap(onApprove)} onReject={wrap(onReject)} busy={busyId === s.id} />
          ))}
        </AnimatePresence>
        {resolved.map((s) => (
          <SignalCard key={s.id} signal={s} onApprove={() => {}} onReject={() => {}} busy={false} />
        ))}
      </div>
    </div>
  );
};
